"use client";
import ArrowDownRightIcon from "@heroicons/react/24/solid/ArrowDownRightIcon";
import MapPinIcon from "@heroicons/react/24/solid/MapPinIcon";
import PhoneIcon from "@heroicons/react/24/solid/PhoneIcon";
import { Button } from "../../ui/button";
import { useEffect, useState } from "react";
import { useVenueDetail } from "@/hooks/venueContext";
import WhatsappButton from "@/components/WhatsappButton";
import {
  WHATSAPP_SECTIONS,
  WHATSAPP_TAGS,
} from "@/@core/enums/whatsapp_tags.enum";
import {
  getDayByDate,
  getDayNumberByDate,
  getHourByDate,
  getMonthByDate,
} from "@/lib/day-formats";
import WhatsappIcon from "@/components/hero-icons/whatsapp-icon";
import dynamic from "next/dynamic";
import { useTranslation } from "@/hooks/translationContext";

const VenueDetailShareModal = dynamic(
  () => import("@/components/views/venue-details/new-details/share/Modal"),
);

interface Props extends Event.Detail {
  onClick?: () => void;
  titleHeadingLevel: "h2" | "h3";
}

export default function EventCard(props: Props) {
  const { t } = useTranslation();
  const { venue, services, recurring } = props;
  const [description] = props.descriptions;
  const [phoneModalIsOpen, setPhoneModalIsOpen] = useState(false);
  const { setVenue } = useVenueDetail();

  const handlePhoneClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    event.preventDefault();

    setPhoneModalIsOpen(true);
    setVenue(props.venue as Venue.List);
  };

  const getHour = () => getHourByDate(props.date);

  useEffect(() => {
    if (!props.venue) return;

    setVenue(props.venue as Venue.List);
  }, [props.venue]);

  const titleClassName =
    "font-bold text-ellipsis line-clamp-[4] text-primary text-base";

  const getHeading = () => {
    if (props.titleHeadingLevel == "h2") {
      return <h2 className={titleClassName}>{description?.title}</h2>;
    }

    return <h3 className={titleClassName}>{description?.title}</h3>;
  };

  return (
    <>
      <article
        onClick={props.onClick}
        role="button"
        tabIndex={0}
        className="cursor-pointer h-full relative w-full p-4 rounded-[24px] border border-gray flex gap-4 min-h-[198px] max-h-[300px]"
      >
        <aside
          className="px-[14px] gap-[6px] rounded-2xl font-bold text-sm min-w-[90px] sm:min-w-[110px] min-h-[185px] border border-assistant-secondary
                    flex flex-col justify-center items-center"
        >
          <span className="text-[44px]">{getDayNumberByDate(props.date)}</span>
          <span className="text-[20px]">{getMonthByDate(props.date, t)}</span>
          <span className="-translate-y-1">{getDayByDate(props.date, t)}</span>
        </aside>

        <main className="flex w-full flex-col gap-2">
          <div className="flex h-full justify-between flex-col gap-2">
            <div>
              {getHeading()}

              <div className="flex flex-wrap gap-2 w-full text-assistant font-medium text-xs items-start">
                <div className="flex flex-wrap gap-2">
                  {Array.isArray(services) &&
                    services?.map((service, index) => (
                        <>
                        {service && (
                          <div key={index} className="h-[17px] px-1.5 py-1 flex justify-center items-center bg-green-200 rounded-2xl">
                            <p className="text-center text-green-700 text-[10px] truncate font-medium font-['DM Sans']">
                              {service.service?.descriptions[0]?.title}
                            </p>
                          </div>
                        )}
                      </>
                    ))}
                </div>
                <p className="whitespace-nowrap">Saat: {getHour()}</p>
              </div>
            </div>

            <footer>
              <div className="flex flex-col gap-1 w-full">
                <div className="flex gap-1 items-center">
                  <ArrowDownRightIcon className="size-3 text-assistant font-medium text-xs" />
                  <p className="text-ellipsis line-clamp-1 text-assistant w-full font-medium text-xs">
                    {venue && venue.descriptions?.[0]?.title}
                  </p>
                </div>

                <div className="flex text-assistant w-full font-medium text-xs gap-1">
                  <MapPinIcon className="size-3" />
                  <p className="text-assistant font-medium">
                    {venue && venue.district?.title},{" "}
                    {venue && venue.district?.city?.title}
                  </p>
                </div>
              </div>

              <div className="flex justify-end pt-2 gap-2">
                <Button
                  className="px-2 !h-6 text-sm"
                  variant="assistant"
                  onClick={(
                    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
                  ) => handlePhoneClick(event)}
                >
                  <PhoneIcon className="size-4" /> Ara
                </Button>

                <WhatsappButton
                  section={WHATSAPP_SECTIONS.EVENT}
                  tag={WHATSAPP_TAGS.EVENT_DETAIL}
                  phoneNumber={venue.whatsappPhoneNumber}
                  venue={venue as Venue.List}
                  className="px-2 !h-6 flex gap-2 text-sm whitespace-nowrap"
                  translateValues={{ event: description?.title }}
                >
                  <WhatsappIcon className="size-4" /> Etkinliği Sor
                </WhatsappButton>
              </div>
            </footer>
          </div>
        </main>
      </article>

      {phoneModalIsOpen && (
        <VenueDetailShareModal
          onClose={() => setPhoneModalIsOpen(false)}
          isShareOpen={phoneModalIsOpen}
        />
      )}
    </>
  );
}
